import { useCallback, useMemo, useRef } from 'react';
import {
  enablePaymentExpirationReminders,
  getHideNotificationsOnParentOffice,
} from '../../../../../growthbook';
import { useIsParentOfficeSelector } from '../../../../../hooks/useIsParentOfficeSelector';
import { useSelectedOffice } from '../../../../../hooks/useSelectedOffice';
import { useAppSelector } from '../../../../../hooks/useAppSelector';

export const useCheckPaymentExpiration = (officeId) => {
  const isParentOffice = useIsParentOfficeSelector();
  const payments = useAppSelector((state) => state.payments.allPayments);
  const user = useSelectedOffice(officeId);

  const isEnablePaymentExpiration = useRef(enablePaymentExpirationReminders()).current;
  const isHideNotificationsOnParentOffice = useRef(getHideNotificationsOnParentOffice()).current;

  const isParentOfficeAndHideNotificationsOnParentOffice =
    isParentOffice && isHideNotificationsOnParentOffice;

  const defaultPaymentMethod = useMemo(
    () => payments?.find((payment) => payment.is_selected),
    [payments],
  );

  const isBankAccount = defaultPaymentMethod?.type === 'bank_account';

  const checkPaymentExpiration = useCallback(
    (payment) => {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;

      const isExpired =
        (payment?.expiry_year < currentYear ||
          (payment?.expiry_year === currentYear && payment?.expiry_month < currentMonth)) &&
        !isBankAccount;

      let isExpiring = false;

      if (!isExpired) {
        const monthsUntilExpiry =
          (payment?.expiry_year - currentYear) * 12 + (payment?.expiry_month - currentMonth);
        isExpiring = monthsUntilExpiry <= 2 && !isBankAccount;
      }

      return {
        isExpired,
        isExpiring,
      };
    },
    [isBankAccount],
  );

  const { isExpired, isExpiring } = useMemo(
    () => checkPaymentExpiration(defaultPaymentMethod),
    [checkPaymentExpiration, defaultPaymentMethod],
  );

  const isInvalidPayment =
    !isParentOffice && !user?.is_pre_approved && (isExpired || !payments?.length);

  if (
    !isEnablePaymentExpiration ||
    user?.is_pre_approved ||
    isParentOfficeAndHideNotificationsOnParentOffice ||
    isBankAccount
  ) {
    return {
      isExpired: false,
      isExpiring: false,
      checkPaymentExpiration,
      payments,
      isInvalidPayment,
    };
  }

  return {
    isExpired,
    isExpiring,
    checkPaymentExpiration,
    payments,
    isInvalidPayment,
  };
};
